import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { Button } from 'components/ui';
import { COMPANY_ONBOARDING_REQUIRED } from 'components/ui/utils/constant';
import { CompanyOnboardingContext } from 'contexts/CompanyOnboardingContext';
import { SessionContext } from 'contexts/SessionContext';
import { Trans } from 'react-i18next';
import { HiChevronLeft, HiChevronRight, HiCheck, HiSparkles } from 'react-icons/hi';
import useDarkMode from 'utils/hooks/useDarkMode';

export default function CompanyOnboardingModalFooter() {
  const {
    close,
    prevStep,
    nextStep,
    currentStep,
    steps,
    currentStepData,
    stepCustomHandlers
  } = useContext(CompanyOnboardingContext);
  const { user, actions } = useContext(SessionContext);
  const [isDarkMode] = useDarkMode();
  
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const currentStepObj = steps[currentStep];

  // Use custom handlers if available, otherwise use default
  const handleNextStep = stepCustomHandlers?.onNextStep || (() => nextStep(currentStepData));
  const handlePrevStep = stepCustomHandlers?.onPrevStep || prevStep;
  const loading = stepCustomHandlers?.loading || false;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className={`flex items-center justify-between gap-2 md:gap-4 px-4 py-4 md:px-6 lg:px-10 lg:py-4 xl:px-12 border-t ${
        isDarkMode 
          ? 'bg-gray-900/50 border-white/10 backdrop-blur-xl' 
          : 'bg-white/50 border-gray-200 backdrop-blur-xl'
      }`}
    >
      {/* Left side - Previous button */}
      <div className="flex items-center gap-2">
        {!isFirstStep && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              variant="plain"
              onClick={handlePrevStep}
              icon={<HiChevronLeft className="text-base md:text-lg" />}
              className={`!font-medium !px-3 !py-2 !text-sm ${
                isDarkMode
                  ? '!text-gray-400 hover:!text-white'
                  : '!text-gray-600 hover:!text-gray-900'
              }`}
            >
              <span className="hidden sm:inline">
                <Trans i18nKey="common.previous">Previous</Trans>
              </span>
            </Button>
          </motion.div>
        )}
      </div>

      {/* Right side - Skip and Continue buttons */}
      <div className="flex items-center gap-2 md:gap-3">
        {/* Skip button - only if optional */}
        {currentStepObj?.isOptional && (
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="hidden sm:block"
          >
            <Button
              variant="plain"
              onClick={handleNextStep}
              className={`!font-semibold !px-4 !py-2 !text-sm ${
                isDarkMode 
                  ? '!text-purple-400 hover:!text-purple-300' 
                  : '!text-purple-600 hover:!text-purple-700'
              }`}
            >
              <Trans i18nKey="common.skip">Skip</Trans>
            </Button>
          </motion.div>
        )}
        
        {/* Main action button */}
        <motion.div
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
        >
          <Button
            variant="solid"
            onClick={handleNextStep}
            icon={
              loading ? undefined : isLastStep ? (
                <HiCheck className="text-base md:text-base" />
              ) : (
                <HiChevronRight className="text-base md:text-base" />
              )
            }
            loading={loading}
            className={`!bg-gradient-to-r !from-purple-600 !to-pink-600 hover:!from-purple-700 hover:!to-pink-700 !text-white !shadow-lg !font-bold !px-5 !py-2.5 md:!px-8 md:!py-3 !rounded-lg md:!rounded-xl !transition-all !text-sm ${
              loading ? '' : 'hover:!shadow-xl hover:!shadow-purple-500/30'
            }`}
          >
            {isLastStep ? (
              <span className="flex items-center gap-1.5 md:gap-2">
                <Trans i18nKey="common.finish">
                  <span className="hidden sm:inline">Finish Setup</span>
                  <span className="sm:hidden">Finish</span>
                </Trans>
              </span>
            ) : (
              <Trans i18nKey="common.continue">
                <span className="hidden sm:inline">Continue</span>
                <span className="sm:hidden">Next</span>
              </Trans>
            )}
          </Button>
        </motion.div>
      </div>
    </motion.div>
  );
}
